/**
 * Visit Notes Repository (Driven Adapter)
 *
 * Makes HTTP calls to the unified /visit-notes API endpoints
 */

import type { IAPIRoutes } from "../../../../apiRoutes";
import type {
  IVisitNotesRepository,
  ICreateVisitNoteRequest,
  IUpdateVisitNoteRequest,
  IVisitNote,
  ISummarizeNarrativeRequest,
  ISummarizeNarrativeResponse,
  ISendEfaxRequest,
  ISendEfaxResponse,
  ISidePeekProfile,
  ISafetyNote,
  ICodeStatus,
  CodeStatusValue,
} from "../../../core/ports/driver/VisitNotesService";

const env = import.meta.env;

export type IVisitNotesRepositoryType = ReturnType<
  typeof VisitNotesRepositoryFactory
>;

export const VisitNotesRepositoryFactory = ({
  APIProvider,
  AuthService,
  APIRoutes,
}: {
  APIProvider: IAPIProvider;
  AuthService: IAuthService;
  APIRoutes: IAPIRoutes;
}): IVisitNotesRepository => {
  const { POST, PATCH, GET } = APIProvider;
  const { getAuthenticationToken } = AuthService;

  /**
   * Create a new visit note
   * POST /clinic/{clinic_id}/patient/{patient_id}/visit-notes
   */
  const createVisitNoteAPI = async (
    request: ICreateVisitNoteRequest
  ): Promise<IVisitNote> => {
    const url = APIRoutes.apiCreateVisitNote
      .replace("{clinic_id}", request.clinic_id.toString())
      .replace("{patient_id}", request.patient_id.toString());

    const payload = {
      clinic_id: request.clinic_id,
      patient_id: request.patient_id,
      doc_type: request.doc_type,
      type: request.type,
      notes: request.notes,
      file_link: request.file_link,
      total_time: request.total_time,
      context: request.context,
      ...(request.shortlisted !== undefined && {
        shortlisted: request.shortlisted,
      }),
      ...(request.created_at && {
        created_at: request.created_at,
      }),
    };

    const response = await POST(url, payload, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });

    return response.data || response;
  };

  /**
   * Update an existing visit note
   * PATCH /clinic/{clinic_id}/patient/{patient_id}/visit-notes/{note_id}
   */
  const updateVisitNoteAPI = async (
    clinicId: number,
    patientId: number,
    noteId: number,
    request: IUpdateVisitNoteRequest
  ): Promise<IVisitNote> => {
    const url = APIRoutes.apiUpdateVisitNote
      .replace("{clinic_id}", clinicId.toString())
      .replace("{patient_id}", patientId.toString())
      .replace("{note_id}", noteId.toString());

    // Include shortlisted if provided
    const updatePayload = {
      ...request,
      ...(request.shortlisted !== undefined && {
        shortlisted: request.shortlisted,
      }),
    };

    const response = await PATCH(url, updatePayload, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });

    return response.data || response;
  };

  /**
   * Get all visit notes for a clinic
   * GET /clinic/{clinic_id}/visit-notes
   */
  const getAllVisitNotesAPI = async (clinicId: number) => {
    const url = APIRoutes.apiGetAllVisitNotes.replace(
      "{clinic_id}",
      clinicId.toString()
    );

    const response = await GET(
      url,
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

    return response.data || response;
  };

  /**
   * Get a single visit note
   * GET /visit-notes/{note_id}
   */
  const getVisitNoteAPI = async (noteId: number): Promise<IVisitNote> => {
    const url = APIRoutes.apiGetSingleVisitNote.replace(
      "{note_id}",
      noteId.toString()
    );

    const response = await GET(
      url,
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

    return response.data || response;
  };

  /**
   * Summarize narrative for sharing
   * POST /healthscribe/summarize-narrative
   */
  const summarizeNarrativeAPI = async (
    request: ISummarizeNarrativeRequest
  ): Promise<ISummarizeNarrativeResponse> => {
    const url = APIRoutes.apiSummarizeNarrative;

    const response = await POST(url, request, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });

    return response.data || response;
  };

  /**
   * Send visit note PDF via eFax
   * POST /clinic/{clinic_id}/visit-notes/efax
   * Uses raw fetch for FormData since APIProvider doesn't handle file uploads properly
   */
  const sendEfaxAPI = async (
    request: ISendEfaxRequest
  ): Promise<ISendEfaxResponse> => {
    try {
      const url = APIRoutes.apiSendVisitNoteEfax.replace(
        "{clinic_id}",
        request.clinicId.toString()
      );

      const formData = new FormData();
      formData.append("file", request.file, request.filename);
      formData.append("efax_email", request.efaxEmail);
      formData.append("patient_name", request.patientName);
      if (request.body) {
        formData.append("body", request.body);
      }

      const authToken = await getAuthenticationToken();

      const response = await fetch(`${env.VITE_API_GATEWAY_URL_V2}${url}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail?.message || `Failed to send eFax: ${response.status}`
        );
      }

      return await response.json();
    } catch (error) {
      console.error("[VisitNotes Repository] Failed to send eFax:", error);
      throw error;
    }
  };

  /**
   * Get Side Peek Profile for a patient
   * GET /clinic/{clinic_id}/patient/{patient_id}/sidepeek-profile
   * Returns both sidepeek_profile and patient_dob
   */
  const getSidePeekProfileAPI = async (
    clinicId: number,
    patientId: number
  ): Promise<{
    sidepeek_profile: ISidePeekProfile | null;
    patient_dob?: string;
  }> => {
    const url = APIRoutes.apiGetSidePeekProfile
      .replace("{clinic_id}", clinicId.toString())
      .replace("{patient_id}", patientId.toString());

    const response = await GET(
      url,
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

    // Extract sidepeek_profile and patient_dob from the response wrapper
    // API returns: { patient_id, sidepeek_profile, exists, patient_dob }
    const data = response.data || response;
    return {
      sidepeek_profile: data.sidepeek_profile || null,
      patient_dob: data.patient_dob,
    };
  };

  /**
   * Trigger Side Peek Profile generation for a patient
   * POST /clinic/{clinic_id}/patient/{patient_id}/sidepeek-profile
   */
  const generateSidePeekProfileAPI = async (
    clinicId: number,
    patientId: number,
    noteId: number,
    section?: "diagnoses" | "medications" | "allergies",
    model?: string
  ): Promise<ISidePeekProfile> => {
    const url = APIRoutes.apiGenerateSidePeekProfile
      .replace("{clinic_id}", clinicId.toString())
      .replace("{patient_id}", patientId.toString());

    const payload: { note_id: number; section?: string; model?: string } = {
      note_id: noteId,
    };
    if (section) payload.section = section;
    if (model) payload.model = model;

    const response = await POST(url, payload, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });

    // Extract sidepeek_profile from the response wrapper
    // API returns: { message, patient_id, sidepeek_profile }
    const data = response.data || response;
    return data.sidepeek_profile || data;
  };

  /**
   * Update Side Peek Profile for a patient (manual edits)
   * PATCH /clinic/{clinic_id}/patient/{patient_id}/sidepeek-profile
   */
  const updateSidePeekProfileAPI = async (
    clinicId: number,
    patientId: number,
    updateData: {
      diagnoses?: Array<{
        value: string;
        source: string | null;
        reference: string | null;
        note_id?: number | null;
        added_at?: string | null;
      }>;
      medications?: Array<{
        value: string;
        source: string | null;
        reference: string | null;
        note_id?: number | null;
        added_at?: string | null;
      }>;
      allergies?: Array<{
        value: string;
        source: string | null;
        reference: string | null;
        note_id?: number | null;
        added_at?: string | null;
      }>;
    }
  ): Promise<ISidePeekProfile> => {
    const url = APIRoutes.apiUpdateSidePeekProfile
      .replace("{clinic_id}", clinicId.toString())
      .replace("{patient_id}", patientId.toString());

    const response = await PATCH(url, updateData, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });

    // Extract sidepeek_profile from the response wrapper
    // API returns: { message, patient_id, sidepeek_profile }
    const data = response.data || response;
    return data.sidepeek_profile || data;
  };

  /**
   * Get Safety Note for a patient
   * GET /clinic/{clinic_id}/patient/{patient_id}/safety-note
   */
  const getSafetyNoteAPI = async (
    clinicId: number,
    patientId: number
  ): Promise<ISafetyNote> => {
    const url = APIRoutes.apiGetSafetyNote
      .replace("{clinic_id}", clinicId.toString())
      .replace("{patient_id}", patientId.toString());

    const response = await GET(
      url,
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

    // Extract safety_note from the response wrapper
    // API returns: { patient_id, safety_note, exists }
    const data = response.data || response;
    return data.safety_note || { content: "" };
  };

  /**
   * Update Safety Note for a patient
   * PATCH /clinic/{clinic_id}/patient/{patient_id}/safety-note
   */
  const updateSafetyNoteAPI = async (
    clinicId: number,
    patientId: number,
    content: string
  ): Promise<ISafetyNote> => {
    const url = APIRoutes.apiUpdateSafetyNote
      .replace("{clinic_id}", clinicId.toString())
      .replace("{patient_id}", patientId.toString());

    const response = await PATCH(
      url,
      { content },
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

    // Extract safety_note from the response wrapper
    // API returns: { message, patient_id, safety_note }
    const data = response.data || response;
    return data.safety_note || { content };
  };

  /**
   * Get Code Status for a patient
   * GET /clinic/{clinic_id}/patient/{patient_id}/code-status
   */
  const getCodeStatusAPI = async (
    clinicId: number,
    patientId: number
  ): Promise<ICodeStatus> => {
    const url = APIRoutes.apiGetCodeStatus
      .replace("{clinic_id}", clinicId.toString())
      .replace("{patient_id}", patientId.toString());

    const response = await GET(
      url,
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

    // Extract code_status from the response wrapper
    // API returns: { patient_id, code_status, exists }
    const data = response.data || response;
    return data.code_status || { status: "NOT_SPECIFIED" };
  };

  /**
   * Update Code Status for a patient
   * PATCH /clinic/{clinic_id}/patient/{patient_id}/code-status
   */
  const updateCodeStatusAPI = async (
    clinicId: number,
    patientId: number,
    status: CodeStatusValue,
    otherStatus?: string | null
  ): Promise<ICodeStatus> => {
    const url = APIRoutes.apiUpdateCodeStatus
      .replace("{clinic_id}", clinicId.toString())
      .replace("{patient_id}", patientId.toString());

    const response = await PATCH(
      url,
      { status, other_status: otherStatus },
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

    // Extract code_status from the response wrapper
    // API returns: { message, patient_id, code_status }
    const data = response.data || response;
    return data.code_status || { status };
  };

  return {
    createVisitNoteAPI,
    updateVisitNoteAPI,
    getAllVisitNotesAPI,
    getVisitNoteAPI,
    summarizeNarrativeAPI,
    sendEfaxAPI,
    getSidePeekProfileAPI,
    generateSidePeekProfileAPI,
    updateSidePeekProfileAPI,
    getSafetyNoteAPI,
    updateSafetyNoteAPI,
    getCodeStatusAPI,
    updateCodeStatusAPI,
  };
};
